<template>
  <p-container>
    <h2 class="mb-4">
      {{ $t('core.page.integrationlog.page.title') }}
    </h2>
    <p-row>
      <p-col xs12 md6 lg2>
        <p-text-field v-model="filter.searchCriteria" clearable context-icon="search" :label="$t('core.app.search')"></p-text-field>
      </p-col>
      <p-col xs12 md6 lg2>
        <p-select
          v-model="filter.integrationTaskId"
          clearable
          item-value="id"
          item-display="name"
          :label="$t('core.page.integrationlog.action.integrationTask')"
          :items="integrationTasks"
        ></p-select>
      </p-col>
      <p-col xs12 md6 lg2>
        <p-select v-model="filter.integrationStatusId" clearable :label="$t('core.page.integrationlog.action.status')" :items="statuses"></p-select>
      </p-col>
      <p-col xs12 md6 lg4>
        <p-date-time-field v-model="filter.dateFrom" :label="$t('core.page.integrationlog.action.from')"></p-date-time-field>
      </p-col>
      <p-col xs12 md6 lg4>
        <p-date-time-field v-model="filter.dateTo" :label="$t('core.page.integrationlog.action.to')"></p-date-time-field>
      </p-col>
    </p-row>
    <p-row>
      <p-col xs12>
        <p-request-alert v-model="requestError"></p-request-alert>
      </p-col>
    </p-row>
    <p-list-view ref="ordersList" v-model="list" :pagination.sync="pagination" :service-function="populateData" :columns="columns">
      <template slot="table-actions">
        <p-button @click="refresh">
          <p-icon>refresh</p-icon>
        </p-button>
        <p-button :loading="isLoading" :disabled="isLoading || !list.length" @click="downloadIntegrationCallReport()">
          <p-icon left>
            download
          </p-icon>
          {{ $t('core.page.integrationlog.action.downloadReport') }}
        </p-button>
      </template>
      <template #entry-actions="{entry}">
        <router-link :to="`/_admin/integrationlog/${entry.id.value}`">
          <p-button>
            <p-icon>find_in_page</p-icon>
          </p-button>
        </router-link>
      </template>
    </p-list-view>
  </p-container>
</template>

<script lang="ts">
  import Vue from 'vue';
  import ResponsePagingInfo from '../../../../plugins/servicelayer/responseTypes/responsePagingInfo';
  import GetIntegrationCallsRequestViewModel from '../../../../services/v1/viewModel/request/IntegrationCalls/GetIntegrationCallsRequestViewModel';
  import IntegrationTaskResourceViewModel from '../../../../services/v1/viewModel/resource/IntegrationTaskResourceViewModel';
  import DownloadIntegrationCallReportRequestModel from '../../../../services/v2/model/download-integration-call-report-request-model';
  import GetAboutRequestModel from '../../../../services/v2/model/get-about-request-model';

  export default Vue.extend({
    auth: true,
    role: ['admin', 'hostadmin'],
    layout: 'layout-hostadmin',
    data: () => ({
      requestError: undefined as any,
      pagination: {} as ResponsePagingInfo,
      filter: {
        searchCriteria: undefined as string | undefined,
        integrationTaskId: undefined as number | undefined,
        integrationStatusId: undefined as number | undefined,
        dateFrom: undefined as string | undefined,
        dateTo: undefined as string | undefined,
      },
      innerPagination: {
        'paging.CurrentPage': undefined as number | undefined,
        'paging.PageSize': undefined as number | undefined,
        'paging.IncludeCount': undefined as boolean | undefined,
        'paging.NoPaging': undefined as boolean | undefined,
      },
      list: [],
      columns: [] as {}[],
      events: [
        { caption: 'Unknown', id: 0 },
      ],
      statuses: [
        { caption: 'Pending', id: 0 },
        { caption: 'In Progress', id: 1 },
        { caption: 'Success', id: 2 },
        { caption: 'Error', id: 3 },
        { caption: 'Completed', id: 4 },
        { caption: 'OnHold', id: 5 },
      ],
      integrationTasks: [] as IntegrationTaskResourceViewModel[],
      isLoading: false,
    }),
    computed: {
      request() {
        return new GetIntegrationCallsRequestViewModel().fromModel({ ...this.filter, ...this.innerPagination });
      },
      querySearchCriteria(): string {
        return this.$router.currentRoute.query.searchCriteria as string;
      },
      queryDateTo(): string {
        return this.$router.currentRoute.query.dateTo as string;
      },
      queryDateFrom(): string {
        return this.$router.currentRoute.query.dateFrom as string;
      },
      queryTaskId(): string {
        return this.$router.currentRoute.query.taskId as string;
      },
      queryStatusId(): string {
        return this.$router.currentRoute.query.statusId as string;
      },
      queryCurrentPage(): string {
        return this.$router.currentRoute.query.currentPage as string;
      },
      queryPageSize(): string {
        return this.$router.currentRoute.query.pageSize as string;
      },
    },
    watch: {
      filter: {
        deep: true,
        handler() {
          this.pagination.currentPage = 1;
          this.innerPagination['paging.CurrentPage'] = 1;
          this.$debounce(() => {
            this.refresh();
          }, 300, this)();
        },
      },
      request() {
        this.writeRequestToQuery();
      },
    },
    beforeMount() {
      this.getQueryFromRequest();
      this.pagination.currentPage = this.innerPagination['paging.CurrentPage'] !;
      this.pagination.pageSize = this.innerPagination['paging.PageSize'] !;
    },
    mounted() {
      this.columns = [
        { value: '', field: 'filter' },
        { value: this.$t('core.page.integrationlog.list.id'), field: 'id' },
        {
          value: this.$t('core.page.integrationlog.list.created'), field: 'created', width: '200px', format: (value: string) => this.$date(value).format('DD.MM.YYYY HH:mm:ss:SSS'),
        },
        { value: this.$t('core.page.integrationlog.list.task'), field: 'integrationTaskName' },
        { value: this.$t('core.page.integrationlog.list.source'), field: 'sourceAppSystemName' },
        {
          value: this.$t('core.page.integrationlog.list.subject'), field: 'integrationObjectSummary', format: (value : any) => this.$format.ellipsis(value, 140),
        },
        { value: this.$t('core.page.integrationlog.list.status'), field: 'integrationStatusName' },
        {
          value: this.$t('core.page.integrationlog.list.message'), field: 'integrationStatusMessage', width: '280px', format: (value: string) => this.$format.ellipsis(value, 140),
        },
        { value: this.$t('core.page.integrationlog.list.runNo'), field: 'runNo', textAlign: 'right' },
      ];
      this.getIntegrationTasks();
    },
    methods: {
      getQueryFromRequest() {
        const queryParams = new GetIntegrationCallsRequestViewModel();
        queryParams.fromQuery(this.$routerUtils.getQueryParams());
        this.filter.searchCriteria = queryParams.searchCriteria;
        this.filter.integrationTaskId = queryParams.integrationTaskId;
        this.filter.integrationStatusId = queryParams.integrationStatusId;
        this.filter.dateFrom = queryParams.dateFrom;
        this.filter.dateTo = queryParams.dateTo;

        this.innerPagination['paging.CurrentPage'] = queryParams['paging.CurrentPage'];
        this.innerPagination['paging.PageSize'] = queryParams['paging.PageSize'];
        this.innerPagination['paging.IncludeCount'] = queryParams['paging.IncludeCount'];
        this.innerPagination['paging.NoPaging'] = queryParams['paging.NoPaging'];
      },
      async writeRequestToQuery() {
        this.$debounce(() => {
          this.$routerUtils.updateQueryParams(this.request.toQuery(), { preserve: false });
        }, 200, this.writeRequestToQuery)();
      },
      refresh() {
        const ordersList = this.$refs.ordersList as any;
        ordersList?.populateList();
      },
      populateData(params: any) {
        this.innerPagination['paging.CurrentPage'] = params.query['paging.CurrentPage'];
        this.innerPagination['paging.PageSize'] = params.query['paging.PageSize'];
        this.innerPagination['paging.IncludeCount'] = params.query['paging.IncludeCount'];
        this.innerPagination['paging.NoPaging'] = params.query['paging.NoPaging'];
        return this.$service.api.core.integrationCalls.getIntegrationCalls(this.request);
      },
      async getIntegrationTasks() {
        try {
          const response = await this.$service.api.core.integrationTasks.getIntegrationTasks();
          this.integrationTasks = response.items;
        } catch (error: any) {
          this.requestError = error;
        }
      },
      async downloadIntegrationCallReport() {
        this.isLoading = true;
        const d = new Date();
        let month = `${d.getMonth() + 1}`;
        let day = `${d.getDate()}`;
        const year = d.getFullYear();
        if (month.length < 2) { month = `0${month}`; }
        if (day.length < 2) { day = `0${day}`; }
        const dateToday = `${year}${month}${day}`;
        try {
          const appAboutTitle = (await this.$service.v2.api.core.app.getAbout(new GetAboutRequestModel())).data.title;
          const req = new DownloadIntegrationCallReportRequestModel();
          req.integrationStatusId = this.filter.integrationStatusId;
          req.dateFrom = this.filter.dateFrom;
          req.dateTo = this.filter.dateTo;
          req.taskId = this.filter.integrationTaskId;
          req.searchCriteria = this.filter.searchCriteria;
          req.customerName = appAboutTitle;
          const reportFile = await this.$service.v2.api.core.integrationCalls.downloadIntegrationCallReport(req);
          const cleanedAppAboutTitle = appAboutTitle?.replace(/[^a-zA-Z0-9]/g, '');
          this.$service.utils.downloadFile(`${dateToday}-${cleanedAppAboutTitle}-ILTransaktionen.xlsx`, reportFile.data);
        } catch (error: any) {
          this.requestError = error;
        }
        this.isLoading = false;
      },
    },
  });
</script>
